import { Fuel, VehicleModelWithInsurancesForD2CDto } from '@orcar/common';
import { filterOptionAtom, useRecoil } from '@orcar/common-d2c';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReservationStepper from '@/components/reservation/ReservationStepper';
import ReserveLoadingOverlay from '@/components/reservation/ReserveLoadingOverlay';
import { useReserveAndPay } from '@/hooks/useReserveAndPay';
import { trackCarDetailedViewed } from '@/utils/analytics';
import { mobonTrackProductView } from '@/utils/mobon';
import { getDefaultInsuranceName } from '@/utils/vehicle-model';
import { SearchResultProps } from '../../components/SearchResult';
import { TopButton } from '../../components/TopButton';
import SearchFilterNull from '../../components/vehicleCard/SearchFilterNull';
import SearchResultCard from '../../components/vehicleCard/SearchResultCard';

interface MobileSearchResultProps extends SearchResultProps {
  filterResult?: VehicleModelWithInsurancesForD2CDto[];
  resultCount: number | undefined;
}

const MobileSearchResult = ({
  filterResult,
  resultCount,
  searchOptions,
  isWithPet,
}: MobileSearchResultProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { reserveAndPay, isReserving } = useReserveAndPay();

  const {
    recoilState: [recoilFilterOption, setRecoilFilterOption],
  } = useRecoil(filterOptionAtom);

  // 예약 마감된 차량만 하단으로 정렬 (서버 정렬 순서 유지)
  const sortedFilterResult = filterResult
    ? [...filterResult].sort((a, b) => {
        // 예약 마감된 차량만 하단으로 내림
        if (a.availableCount === 0 && b.availableCount > 0) return 1;
        if (a.availableCount > 0 && b.availableCount === 0) return -1;
        return 0;
      })
    : filterResult;

  // URL의 carType/carFuel을 recoil에 복원한다(웹 SearchResult과 동일).
  // 펫 모드("펫과 함께")에서는 복원하지 않으며, URL에 값이 없으면 기존 recoil을
  // 덮어쓰지 않는다. (탭 클릭으로 막 설정한 carType이, 펫 해제로 searchParams가
  // 바뀌는 순간 []로 초기화돼 "한 번 더 눌러야" 적용되던 문제를 막는다)
  useEffect(() => {
    const carType = searchParams.get('carType');
    const carFuel = searchParams.get('carFuel');
    const isWithPet = searchParams.get('isWithPet') === 'true';

    if (!isWithPet && (carType || carFuel)) {
      const newFilterOption = { ...recoilFilterOption };
      if (carType) {
        newFilterOption.carTypeList = [carType];
      }
      if (carFuel) {
        newFilterOption.carFuelList = [carFuel];
      }
      setRecoilFilterOption(newFilterOption);
    }
  }, [searchParams, setRecoilFilterOption]);

  // recoil의 carType/carFuel을 URL에 반영한다(웹 SearchResult과 동일).
  // 펫 모드에서는 URL에서 제거해 isWithPet과 carType/carFuel이 섞이지 않게 한다.
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    const isWithPet = newSearchParams.get('isWithPet') === 'true';

    if (isWithPet) {
      newSearchParams.delete('carType');
      newSearchParams.delete('carFuel');
    } else {
      if (recoilFilterOption.carTypeList.length === 1) {
        newSearchParams.set('carType', recoilFilterOption.carTypeList[0]);
      } else {
        newSearchParams.delete('carType');
      }

      if (
        recoilFilterOption.carFuelList.length === 1 &&
        recoilFilterOption.carFuelList.includes(Fuel.EV)
      ) {
        newSearchParams.set('carFuel', Fuel.EV);
      } else {
        newSearchParams.delete('carFuel');
      }
    }

    setSearchParams(newSearchParams, { replace: true });
  }, [
    recoilFilterOption.carTypeList,
    recoilFilterOption.carFuelList,
    searchParams,
    setSearchParams,
  ]);

  const handleReserve = (
    vehicleModel: VehicleModelWithInsurancesForD2CDto,
    insuranceName?: string,
  ) => {
    if (isReserving) return;

    trackCarDetailedViewed({
      car_id: String(vehicleModel.id),
      car_model: vehicleModel.name,
      car_type: vehicleModel.type,
      is_with_pet: !!isWithPet,
      source: 'search_result',
    });

    // 모비온 상품(차량) 수집 — 다이나믹 리타겟팅
    const finalPrice = vehicleModel.insurances?.[0]?.finalPrice;
    const hasAnchor = vehicleModel.referencePrice > 0;
    mobonTrackProductView({
      productCode: String(vehicleModel.id),
      productName: vehicleModel.name,
      price: hasAnchor ? vehicleModel.referencePrice : finalPrice,
      dcPrice: hasAnchor ? finalPrice : undefined,
      imageUrl: vehicleModel.imageUrls?.[0],
      topCategory: vehicleModel.type,
    });

    // 검색 카드의 보험 카드를 선택하면 그 보험으로, 아니면 기본 보험으로
    // 사전예약 후 상세/결제 단계에서 보험을 변경할 수 있다.
    reserveAndPay({
      vehicleModelId: vehicleModel.id,
      carType: vehicleModel.type,
      insuranceName: insuranceName ?? getDefaultInsuranceName(vehicleModel),
      pickUpAt: searchOptions.pickUpAt,
      dropOffAt: searchOptions.dropOffAt,
      isWithPet: !!isWithPet,
      source: 'search_result',
    });
  };

  return (
    <div className='flex h-full flex-col gap-3'>
      {isReserving && <ReserveLoadingOverlay />}
      <ReservationStepper currentStep={2} />
      {resultCount === 0 ? (
        <SearchFilterNull />
      ) : (
        sortedFilterResult?.map((vehicleModel) => (
          <SearchResultCard
            key={vehicleModel.id}
            vehicleModel={vehicleModel}
            unavailable={vehicleModel.availableCount === 0}
            isMobile
            onReserve={handleReserve}
          />
        ))
      )}
      {resultCount !== 0 && <TopButton />}
    </div>
  );
};

export default MobileSearchResult;
